import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { linhaDe, podeAbater } from '@domain/finance'
import type { OffsetLink, Transaction } from '@domain/finance'
import { addDays, dayAndMonth } from '@shared/date/civil'
import { sumCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { normalizedText } from '@domain/product'
import { FolhaDeBaixo } from '@ui/shell/FolhaDeBaixo'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { ANTES_DO_GASTO, DEPOIS_DO_GASTO, comprasCandidatas, mesesDaJanela } from './candidatas'
import type { CompraCandidata } from './candidatas'

/**
 * "Abater um gasto" — escolher, a partir de uma entrada, a compra que ela
 * devolve. A compra parcelada aparece uma vez, com o total. A compra em que
 * esta entrada não cabe aparece apagada, dizendo quanto ainda cabe: escondê-la
 * faria a pessoa procurar uma compra que está ali.
 */
export function FolhaAbaterUmGasto({
  entrada,
  onFechar,
  onGravou,
}: {
  entrada: Transaction
  onFechar: () => void
  onGravou: (offsetOf: OffsetLink) => void
}) {
  const { transactionRepository } = useFinanceRepositories()
  const [lidas, setLidas] = useState<Transaction[] | 'carregando' | 'falhou'>('carregando')
  const [busca, setBusca] = useState('')
  const [falhou, setFalhou] = useState(false)
  const [naoCabe, setNaoCabe] = useState<string | null>(null)

  const data = entrada.source.transactionDate
  const meses = useMemo(
    () => mesesDaJanela(addDays(data, -DEPOIS_DO_GASTO), addDays(data, ANTES_DO_GASTO)),
    [data],
  )

  useEffect(() => {
    let vivo = true
    void Promise.all(meses.map((m) => transactionRepository.listByCompetence(m)))
      .then((porMes) => {
        if (vivo) setLidas(porMes.flat())
      })
      .catch((erro: unknown) => {
        console.error('FolhaAbaterUmGasto: a leitura falhou', erro)
        if (vivo) setLidas('falhou')
      })
    return () => {
      vivo = false
    }
  }, [transactionRepository, meses])

  const termo = normalizedText(busca)
  const compras =
    lidas === 'carregando' || lidas === 'falhou'
      ? []
      : comprasCandidatas(lidas, entrada).filter(
          (c) => termo === '' || normalizedText(c.descricao).includes(termo),
        )

  /**
   * **Confere de novo antes de gravar.** O "cabe" da lista vem do abatimento
   * que o gatilho gravou nas linhas, e ele atrasa um instante: ligar duas
   * entradas em seguida à mesma compra passava do valor dela. Aqui a conta é
   * das entradas ligadas AGORA.
   */
  const escolher = async (c: CompraCandidata) => {
    const offsetOf: OffsetLink = {
      purchaseKey: c.chave,
      transactionId: c.transacao.id,
      description: c.descricao,
    }
    setFalhou(false)
    setNaoCabe(null)
    try {
      const ligadas = await transactionRepository.listOffsetEntries(c.chave)
      const jaLigado = sumCents(
        ligadas
          .filter((e) => e.id !== entrada.id && podeAbater(linhaDe(e)))
          .map((e) => e.source.amountCents),
      )
      const cabe = c.valorCents - jaLigado
      if (entrada.source.amountCents > cabe) {
        setNaoCabe(
          `Esta entrada não cabe mais em ${c.descricao}: cabem ${formatBRL(Math.max(0, cabe))}.`,
        )
        return
      }
      await transactionRepository.setOffsetLinks([{ id: entrada.id, offsetOf }])
      onGravou(offsetOf)
    } catch {
      setFalhou(true)
    }
  }

  // Pelo portal, no `body`: o detalhe mora num vidro com `backdrop-filter`, que
  // vira a referência de todo `fixed` lá dentro (decisão 242e) — renderizada no
  // lugar, a folha ficava no fim da página, atrás da barra de abas. Mesmo
  // arranjo de `EscolhaEmTelaCheia`.
  return createPortal(
    <FolhaDeBaixo titulo="Abater um gasto" onFechar={onFechar}>
      <div className="flex flex-col gap-3 p-1">
        <div>
          <h2 className="titulo text-lg">Abater um gasto</h2>
          <p className="mt-0.5 text-xs text-[var(--text-muted)]">
            {`${entrada.source.description} · ${formatBRL(entrada.source.amountCents)}`}
          </p>
        </div>
        <input
          type="search"
          value={busca}
          placeholder="Buscar pela descrição"
          aria-label="Buscar gasto"
          className="min-h-11 w-full rounded-lg border border-[var(--border)] bg-transparent px-3 text-sm"
          onChange={(e) => {
            setBusca(e.target.value)
          }}
        />
        {lidas === 'carregando' && (
          <p role="status" className="text-sm text-[var(--text-muted)]">Procurando gastos…</p>
        )}
        {lidas === 'falhou' && (
          <p role="alert" className="text-sm">Não foi possível carregar os gastos.</p>
        )}
        <ul>
          {compras.map((c) => {
            const cabe = c.cabeCents >= entrada.source.amountCents
            return (
              <li key={c.chave} className="border-b border-[var(--border)] last:border-b-0">
                <button
                  type="button"
                  disabled={!cabe}
                  onClick={() => {
                    void escolher(c)
                  }}
                  className="flex min-h-11 w-full items-center gap-2 text-left text-sm disabled:opacity-50"
                >
                  <span className="min-w-0 flex-1 truncate">
                    {`${c.descricao} · ${dayAndMonth(c.transacao.source.transactionDate)}`}
                    {c.parcelas !== null && ` · em ${String(c.parcelas)}x`}
                  </span>
                  <span className="tabular-nums">
                    {cabe ? formatBRL(-c.valorCents) : `cabe ${formatBRL(c.cabeCents)}`}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        {naoCabe !== null && (
          <p role="alert" className="text-sm" style={{ color: 'var(--pendente)' }}>
            {naoCabe}
          </p>
        )}
        {falhou && (
          <p role="alert" className="text-sm text-[var(--text-muted)]">Não foi possível salvar agora.</p>
        )}
        <button type="button" onClick={onFechar} className="alvo self-start px-3 text-sm text-[var(--text-muted)]">
          Cancelar
        </button>
      </div>
    </FolhaDeBaixo>,
    document.body,
  )
}
